import React from 'react';
import { graphql, StaticQuery } from 'gatsby';
import styled, { keyframes } from 'styled-components';
import { headShake } from 'react-animations';
import ScrollAnimation from 'react-animate-on-scroll';
import GatsbyImage from 'gatsby-image';

import Layout from '../components/layout';
import SEO from '../components/seo';
import PostCard from '../components/postCard';
import MailtoLink from '../components/MailtoLink';

import '../utils/normalize.css';
import '../utils/css/screen.css';

import { Icon, InlineIcon } from '@iconify/react';
import pythonIcon from '@iconify/icons-cib/python';
import kubernetesIcon from '@iconify/icons-cib/kubernetes';
import reactIcon from '@iconify/icons-cib/react';
import pytorchIcon from '@iconify/icons-cib/pytorch';
import googleDevelopersIcon from '@iconify/icons-logos/google-developers-icon';
import acmIcon from '@iconify/icons-cib/acm';

const featuredProjectDirs = new Set([
  'cms-workflow-operations',
  'archi',
  'argusa-ai-challenge-2025',
  'cloud-canvas-ai',
  'llm-dev-mate',
  'gemini-alchemy'
]);

const BlogIndex = ({ data }, location) => {
  const siteTitle = data.site.siteMetadata.title;
  const posts = [...data.allFile.nodes].sort((a, b) => {
    const dateA = a.children[0]?.frontmatter?.date;
    const dateB = b.children[0]?.frontmatter?.date;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return new Date(dateB) - new Date(dateA);
  });
  const HeadShake = styled.div`
    animation: 2s ${keyframes`${headShake}`};
  `;
  let postCounter = 0;

  return (
    <Layout title={siteTitle}>
      <SEO
        title="M. Hassan Ahmed · Inference Platform Engineer"
        titleTemplate="%s"
        description="Muhammad Hassan Ahmed (Hassan Ahmed) is an Inference Platform Engineer at Lyceum in Zurich and a former CERN software engineer, writing about LLM agents, RAG and production ML."
        keywords={[
          'python',
          'developer',
          'AI',
          'machine learning',
          'ml',
          'devlog',
          'blog',
          'gatsby',
          'javascript',
          'react',
          'hassan',
          'hassan11196',
          'hassanahmed19',
          'hassan fast',
          'hassan ahmed',
          '11196',
          'LLM',
          'RAG',
          'AI Engineer',
          'Full Stack Engineer',
          'Hassan Ahmed AI',
          'Muhammad Hassan Ahmed',
          'Hassan Software Engineer',
          'Hassan Ahmed Software Engineer'
        ]}
      />

      {data.site.siteMetadata.description && (
        <header className="page-head page-head-hero">
          <h1 className="page-head-title">
            AI engineer and full-stack builder.
            <br />
            LLM agents, RAG systems, and production ML.
          </h1>
          {data.site.siteMetadata.subtitle && (
            <p className="page-head-subtitle">{data.site.siteMetadata.subtitle}</p>
          )}
          <div className="hero-ctas">
            <a className="hero-cta" href="#projects">
              View projects <span aria-hidden="true">↓</span>
            </a>
            <MailtoLink
              className="hero-cta"
              encoded={data.site.siteMetadata.emailEncoded}
            >
              Email
            </MailtoLink>
            {data.site.siteMetadata.resume && (
              <a
                className="hero-cta"
                href={data.site.siteMetadata.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                CV
              </a>
            )}
          </div>
          <div
            className="row hero-tech-row"
            role="list"
            aria-label="Primary tech stack"
          >
            <div className="col" role="listitem">
              <HeadShake>
                <Icon
                  width="40"
                  height="40"
                  icon={pythonIcon}
                  aria-label="Python"
                />
              </HeadShake>
            </div>
            <div className="col" role="listitem">
              <HeadShake>
                <Icon
                  width="40"
                  height="40"
                  icon={pytorchIcon}
                  aria-label="PyTorch"
                />
              </HeadShake>
            </div>
            <div className="col" role="listitem">
              <HeadShake>
                <Icon width="40" height="40" icon={reactIcon} aria-label="React" />
              </HeadShake>
            </div>
            <div className="col" role="listitem">
              <HeadShake>
                <Icon
                  width="40"
                  height="40"
                  icon={kubernetesIcon}
                  aria-label="Kubernetes"
                />
              </HeadShake>
            </div>
          </div>
        </header>
      )}

      <section id="projects" className="home-section home-section-projects">
        <h2 className="home-section-heading">Selected work</h2>
        <div className="post-feed">
          {posts
            .filter(node => {
              if (node.sourceInstanceName === 'blog') return false;
              if (node.sourceInstanceName !== 'project') return true;
              return featuredProjectDirs.has(node.relativeDirectory);
            })
            .map(node => {
              postCounter++;
              return (
                <PostCard
                  key={node.children[0].fields.slug}
                  count={postCounter}
                  node={node.children[0]}
                  sourceInstanceName={node.sourceInstanceName}
                  postClass={`post`}
                />
              );
            })}
        </div>
      </section>

      {data.site.siteMetadata.description && (
        <section className="home-section home-section-experience">
          <h2 className="home-section-heading">Experience</h2>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <GatsbyImage
                  className="lyceum-mark logo-for-dark"
                  fluid={data.lyceumMarkWhite.nodes[0].childImageSharp.fluid}
                  alt="Lyceum"
                />
                <GatsbyImage
                  className="lyceum-mark logo-for-light"
                  fluid={data.lyceumMark.nodes[0].childImageSharp.fluid}
                  alt="Lyceum"
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>Inference Platform Engineer — Lyceum</h3>
              <p className="resume-meta">
                Aug 2026 – Present · Zurich, Switzerland
              </p>
              <p>Dedicated Inference for Open Models.</p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <GatsbyImage
                  width="60x"
                  height="60x"
                  fluid={data.cernLogo.nodes[0].childImageSharp.fluid}
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>Software Engineer — CERN, CMS Offline &amp; Computing</h3>
              <p className="resume-meta">Aug 2023 – Jul 2026 · Geneva, Switzerland</p>
              <p>
                Developed WMCore and Unified for CMS Monte Carlo simulations and
                reconstruction across the WLCG. Built React + FastAPI ops dashboards,
                automated deployments with Kubernetes/ArgoCD, and managed Oracle,
                Mongo, and MySQL DBs with OpenSearch/Grafana observability.
              </p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <GatsbyImage
                  width="120x"
                  height="60x"
                  fluid={data.retroLogo.nodes[0].childImageSharp.fluid}
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>Research Engineer (CV &amp; ML) — Retrocausal</h3>
              <p className="resume-meta">
                Sep 2021 – Jul 2023 · Redmond, WA (Remote)
              </p>
              <p>
                Shipped SOTA activity-recognition and action-segmentation models into
                a PyQt desktop client with a Django + VueJS stack on AWS, improving
                factory-floor accuracy metrics by 10%.
              </p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <GatsbyImage
                  width="60x"
                  height="60x"
                  fluid={data.cernLogo.nodes[0].childImageSharp.fluid}
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>OpenLab Summer Student — CERN BioDynaMo</h3>
              <p className="resume-meta">Jun 2021 – Aug 2021 · Remote</p>
              <p>
                Automated CI/CD with Docker + GitHub Actions and refreshed the
                publishing pipeline from Binder to JupyterLab for the BioDynaMo
                agent-based simulation platform.
              </p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <GatsbyImage
                  width="70x"
                  height="70x"
                  fluid={data.nucesLogo.nodes[0].childImageSharp.fluid}
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>BS Computer Science — FAST-NUCES</h3>
              <p className="resume-meta">
                Aug 2017 – Jul 2021 · Karachi, Pakistan · Magna Cum Laude
              </p>
              <p>
                Graduated with a 3.78 CGPA. Rector and Dean's List honours, focusing
                on distributed systems, ML, and large-scale web engineering.
              </p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <InlineIcon box={true} width="90%" length="90%" icon={acmIcon} />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>Publications — ECCV 2024 · ISMAR 2022 · BioDynaMo 2021</h3>
              <p>
                Co-authored work on aligning 2D skeleton sequences with
                multi-modality fusion, AR job-status tracking, and BioDynaMo lab
                automation.
              </p>
            </div>
          </div>

          <div className="row timeline-row">
            <div className="col-2">
              <ScrollAnimation animateIn="bounceInRight">
                <InlineIcon
                  box={true}
                  width="90%"
                  length="90%"
                  icon={googleDevelopersIcon}
                />
              </ScrollAnimation>
            </div>
            <div className="col-10">
              <h3>Awards &amp; hackathons</h3>
              <p>
                2024 CMS Award for modernising Unified operations. Runner-up, Argusa
                AI Challenge 2025 at EPFL. 2nd prize, GovTech Hackathon 2021 (World
                Bank + Code for Pakistan). Finalist, CERN Webfest 2021.
              </p>
            </div>
          </div>
        </section>
      )}

      {data.site.siteMetadata.description && (
        <section className="home-section home-section-contact">
          <h2 className="home-section-heading">Get in touch</h2>
          <div className="contact-row">
            <MailtoLink
              className="contact-link"
              encoded={data.site.siteMetadata.emailEncoded}
            >
              Send me an email
            </MailtoLink>
            <a
              className="contact-link"
              href="https://www.linkedin.com/in/hassan11196/"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
            <a
              className="contact-link"
              href="https://www.github.com/hassan11196"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
            {data.site.siteMetadata.resume && (
              <a
                className="contact-link"
                href={data.site.siteMetadata.resume}
                target="_blank"
                rel="noopener noreferrer"
              >
                CV
              </a>
            )}
          </div>
        </section>
      )}
    </Layout>
  );
};

const indexQuery = graphql`
  query {
    nucesLogo: allFile(
      limit: 100
      filter: { sourceInstanceName: { eq: "assets" }, name: { eq: "nuces" } }
    ) {
      totalCount
      nodes {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid
          }
        }
        name
      }
    }
    lyceumMarkWhite: allFile(
      limit: 1
      filter: {
        sourceInstanceName: { eq: "assets" }
        name: { eq: "lyceum-mark-white" }
      }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 256) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
    lyceumMark: allFile(
      limit: 1
      filter: { sourceInstanceName: { eq: "assets" }, name: { eq: "lyceum-mark" } }
    ) {
      nodes {
        childImageSharp {
          fluid(maxWidth: 256) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
    cernLogo: allFile(
      limit: 100
      filter: {
        sourceInstanceName: { eq: "assets" }
        name: { eq: "cern-logo-trans" }
      }
    ) {
      totalCount
      nodes {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid
          }
        }
        name
      }
    }
    retroLogo: allFile(
      limit: 100
      filter: {
        sourceInstanceName: { eq: "assets" }
        name: { eq: "retro-log-cropped" }
      }
    ) {
      totalCount
      nodes {
        childImageSharp {
          fluid(maxWidth: 1200) {
            ...GatsbyImageSharpFluid
          }
        }
        name
      }
    }
    site {
      siteMetadata {
        title
        description
        subtitle
        emailEncoded
        location
        availability
        resume
      }
    }
    allMarkdownRemark(sort: { fields: [frontmatter___date], order: DESC }) {
      edges {
        node {
          excerpt
          fields {
            slug
          }
          frontmatter {
            date
            title
            description
            tags
            thumbnail {
              childImageSharp {
                fluid(maxWidth: 1360) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
      }
    }
    allFile(filter: { ext: { eq: ".md" } }) {
      nodes {
        id
        children {
          id
          ... on MarkdownRemark {
            id
            fields {
              slug
            }
            frontmatter {
              title
              date
              description
              tags
              thumbnail {
                childImageSharp {
                  fluid(maxWidth: 1360) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
        relativePath
        relativeDirectory
        ext
        sourceInstanceName
      }
    }
  }
`;

export default props => (
  <StaticQuery
    query={indexQuery}
    render={data => (
      <BlogIndex location={props.location} props data={data} {...props} />
    )}
  />
);
